import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line,
} from "recharts";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const PROJECT_COLORS = ["#0EA5E9", "#F97316", "#22C55E", "#8B5CF6", "#EC4899", "#EAB308", "#14B8A6", "#64748B", "#DC2626", "#0284C7", "#65A30D", "#9333EA"];
const LINE_COLORS = PROJECT_COLORS;

function colorFor(name, legend) {
  const i = legend.indexOf(name);
  return PROJECT_COLORS[(i < 0 ? 0 : i) % PROJECT_COLORS.length];
}

/** Admin/founder-only "Insights" tab for Timesheet — mirrors the Leaves
 * Insights suite's visual style: Team Distribution (stacked horizontal bar,
 * sorted desc) + Monthly Distribution (top-8 lines, or one selected person's
 * stacked-by-project bars). Stacked by PROJECT name instead of a fixed enum,
 * since projects are dynamic — the legend only lists projects that actually
 * have entries in the selected period/month. */
export default function TimesheetInsights() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState("all");

  const load = async (y) => {
    setLoading(true);
    try {
      const { data: insights } = await api.get("/timesheet/insights", { params: { year: y } });
      setData(insights);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); }, [year]);

  const teamChartData = useMemo(() => {
    if (!data?.team_distribution) return [];
    return data.team_distribution.map((r) => ({ name: r.name, ...r.by_project, total: r.total_hours }));
  }, [data]);

  const topPeople = useMemo(() => (data?.team_distribution || []).slice(0, 8).map((r) => r.user_id), [data]);
  const monthlyChartData = useMemo(() => {
    if (!data?.monthly_distribution) return [];
    if (selectedUser !== "all") {
      const byMonth = data.monthly_by_project?.[selectedUser] || {};
      return Object.keys(byMonth).sort().map((m) => ({ month: m, ...byMonth[m] }));
    }
    return data.monthly_distribution.map((row) => {
      const out = { month: row.month };
      topPeople.forEach((uid) => { out[data.user_names[uid]] = row.by_user[uid] || 0; });
      return out;
    });
  }, [data, selectedUser, topPeople]);

  // Legend for the selected person's stacked view — only projects that have
  // ANY entry across their months this year (so a project with zero hours
  // for them never shows, and a month with no entries for a project simply
  // renders a zero-height segment for that bar, i.e. doesn't appear).
  const individualProjectLegend = useMemo(() => {
    if (selectedUser === "all") return [];
    const set = new Set();
    Object.values(data?.monthly_by_project?.[selectedUser] || {}).forEach((projs) => Object.keys(projs).forEach((p) => set.add(p)));
    return [...set].sort();
  }, [data, selectedUser]);

  const peopleForSelect = data?.team_distribution || [];
  const legend = data?.project_legend || [];

  return (
    <div className="space-y-6" data-testid="timesheet-insights">
      <div className="flex items-center justify-between">
        <div className="label-overline">Insights · {loading ? "loading…" : year}</div>
        <div className="inline-flex items-center gap-1">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                y === year ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`ts-insights-year-${y}`}
            >{y}</button>
          ))}
        </div>
      </div>

      {/* Team Distribution */}
      <div className="card-flat p-4" data-testid="chart-ts-team-distribution">
        <div className="text-sm font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5"><Users size={14} />Team hours distribution</div>
        <div className="text-xs text-slate-500 mb-3">Total hours logged per person this year, stacked by project — highest first.</div>
        <ResponsiveContainer width="100%" height={Math.max(220, teamChartData.length * 34)}>
          <BarChart data={teamChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} fontSize={11} unit="h" />
            <YAxis type="category" dataKey="name" width={120} fontSize={11} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {legend.map((p) => (
              <Bar key={p} dataKey={p} stackId="a" fill={colorFor(p, legend)} name={p} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {teamChartData.length === 0 && !loading && <div className="text-xs text-slate-400 italic py-4 text-center">No hours logged in {year} yet.</div>}
      </div>

      {/* Individual Breakdown toggle + Monthly Project Distribution */}
      <div className="card-flat p-4" data-testid="chart-ts-monthly-distribution">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5"><TrendingUp size={14} />Monthly project distribution</div>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-56" data-testid="ts-insights-individual-select"><SelectValue placeholder="Everyone (top 8)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone (top 8 by volume)</SelectItem>
              {peopleForSelect.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mb-3">
          {selectedUser === "all" ? "Total hours logged by the top people, month by month." : "Selected person's hours by project, month by month — only projects they logged that month appear."}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          {selectedUser === "all" ? (
            <LineChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} unit="h" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {topPeople.map((uid, i) => (
                <Line key={uid} type="monotone" dataKey={data?.user_names?.[uid]} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          ) : (
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} unit="h" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {individualProjectLegend.map((p) => (
                <Bar key={p} dataKey={p} stackId="a" fill={colorFor(p, legend)} name={p} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
        {selectedUser !== "all" && monthlyChartData.length === 0 && (
          <div className="text-xs text-slate-400 italic py-4 text-center">No hours logged by this person in {year}.</div>
        )}
      </div>
    </div>
  );
}
